/** Keys that only change another key — held alone, nobody is using the keyboard yet. */
const MODIFIERS = new Set(["Meta", "Control", "Alt", "Shift", "CapsLock", "Fn"]);

/**
 * How the page was last used, on the root as `data-eingabe`: „tastatur"
 * after a key, „zeiger" after a press of mouse, pen or finger, nothing
 * before either (Task 12, from Task 5). A screen puts the focus on its title
 * when it opens — a place to stand — and Chrome draws the focus ring for a
 * script's focus before anything was pressed in the page: a title reached
 * by a link from outside or after a reload wore a ring. base.css draws that
 * ring only once the keyboard is in use. A shortcut held with a modifier
 * (⌘C, Strg+0) does not count as the keyboard taking over.
 *
 * Returns the function that stops listening.
 */
export function trackInputModality(root: HTMLElement = document.documentElement): () => void {
  // Written only when it changes (final review, M-10): every key and every
  // press would otherwise touch an attribute on the root, which a selector
  // in base.css reads — a style recalculation of the whole page per key.
  const say = (how: "tastatur" | "zeiger") => {
    if (root.dataset.eingabe !== how) root.dataset.eingabe = how;
  };
  const keyboard = (event: KeyboardEvent) => {
    if (event.metaKey || event.ctrlKey || event.altKey || MODIFIERS.has(event.key)) return;
    say("tastatur");
  };
  const pointer = () => say("zeiger");
  document.addEventListener("keydown", keyboard, true);
  document.addEventListener("pointerdown", pointer, true);
  return () => {
    document.removeEventListener("keydown", keyboard, true);
    document.removeEventListener("pointerdown", pointer, true);
  };
}
